"use client"

import type { ChainArch } from "@opensea/graphql"
import { useMemo } from "react"
import { useConnections as useWagmiConnections } from "wagmi"
import { isAbstractWalletAccount } from "../../lib/wagmi/helpers"
import type { Connector } from "../../types"
import { useConnectors } from "./useConnectors"
import { useSolanaConnections } from "./useSolanaConnections"

export type Connection = {
  connector: Connector
  chainId: number
  chainArch: ChainArch
  addresses: string[]
}

export function useConnections(): Connection[] {
  const { getConnector } = useConnectors()
  const wagmiConnections = useWagmiConnections()
  const { connections: solanaConnections } = useSolanaConnections()

  const evmConnections = useMemo(() => {
    const connections: Connection[] = []

    wagmiConnections.forEach(connection => {
      const connector = getConnector(connection.connector.id)

      if (!connector) {
        return
      }

      let addresses = connection.accounts.map(account => account)

      // Guard against abstract wallet's signer address
      if (isAbstractWalletAccount(connector.id)) {
        addresses = [addresses[0]]
      }

      // Guard against duplicate connectors
      if (connections.some(c => c.connector.id === connector.id)) {
        return
      }

      connections.push({
        connector,
        chainId: connection.chainId,
        addresses,
        chainArch: "EVM" as const,
      })
    })

    return connections
  }, [wagmiConnections])

  return [...solanaConnections, ...evmConnections]
}
